// 아이템 관리자 클래스
class ItemManager {
  constructor(scene, camera, player) {
    this.scene = scene;
    this.camera = camera;
    this.player = player;

    // 활성 아이템들
    this.items = [];

    // 아이템 생성 제어 플래그
    this.isSpawningEnabled = true; // 아이템 생성 활성화 여부

    // 동료 관리자 참조 (하트 아이템 회복용)
    this.companionManager = null;

    // 아이템 클래스 매핑
    this.itemClassMap = {
      heart: Heart,
      bomb: Bomb,
      star: Star,
      diamond: Diamond,
      magnet: Magnet,
    };

    // 아이템 생성 설정 - ItemConfig에서 가져옴
    this.spawnSettings = {};
    Object.keys(ItemConfig.SPAWN).forEach((itemType) => {
      this.spawnSettings[itemType] = {
        ...ItemConfig.SPAWN[itemType],
        class: this.itemClassMap[itemType], // 클래스 매핑 추가
      };
    });

    // 카메라 설정
    this.cameraViewSize = GameConfig.CAMERA.VIEW_SIZE || 15;
    this.spawnMargin = 3; // 카메라 경계에서 얼마나 떨어진 곳에 생성할지

    Utils.log("아이템 매니저가 초기화되었습니다.");
  }

  // 매 프레임 업데이트
  update(deltaTime) {
    const currentTime = performance.now();

    // 기존 아이템들 업데이트
    this.updateItems(deltaTime);

    // 플레이어와의 충돌 검사
    this.checkCollisions();

    // 새로운 아이템 생성 검사
    this.checkSpawning(currentTime);

    // 수집된 아이템들 정리
    this.cleanupCollectedItems();
  }

  // 기존 아이템들 업데이트
  updateItems(deltaTime) {
    this.items.forEach((item) => {
      if (item.isLoaded && !item.isCollected) {
        // 다이아몬드인 경우 플레이어 위치를 전달해서 자석 효과가 작동하도록 함
        if (item instanceof Diamond) {
          item.update(deltaTime, this.player ? this.player.position : null);
        } else {
          item.update(deltaTime);
        }
      }
    });
  }

  // 플레이어와의 충돌 검사
  checkCollisions() {
    if (!this.player || !this.player.isAlive) return;

    this.items.forEach((item) => {
      if (item.isLoaded && !item.isCollected) {
        if (item.checkCollision(this.player.position)) {
          // 자석 아이템인 경우 특별 처리
          if (item instanceof Magnet) {
            item.setItemManager(this); // ItemManager 참조 전달
            item.collect(this.player);
          } else if (item instanceof Heart) {
            // 하트 아이템인 경우 CompanionManager 전달
            item.companionManager = this.companionManager;
            item.collect(this.player);
          } else {
            // 다른 아이템들은 표준 수집 방식 사용
            item.collect(this.player);
          }
        }
      }
    });
  }

  // 새로운 아이템 생성 검사
  checkSpawning(currentTime) {
    // 생성이 비활성화된 경우 아이템을 생성하지 않음
    if (!this.isSpawningEnabled) return;

    Object.entries(this.spawnSettings).forEach(([itemType, settings]) => {
      // 생성 시간 체크
      if (currentTime - settings.lastSpawnTime >= settings.spawnInterval) {
        // 확률 체크
        if (Math.random() < settings.spawnChance) {
          // 최대 개수 체크
          const currentCount = this.getItemCountByType(itemType);
          if (currentCount < settings.maxCount) {
            this.spawnItem(itemType, settings);
            settings.lastSpawnTime = currentTime;
          }
        } else {
          // 확률에 실패해도 시간은 리셋 (다음 기회를 위해)
          settings.lastSpawnTime = currentTime;
        }
      }
    });
  }

  // 특정 타입의 아이템 개수 확인
  getItemCountByType(itemType) {
    const ItemClass = this.spawnSettings[itemType].class;
    return this.items.filter(
      (item) => item instanceof ItemClass && !item.isCollected
    ).length;
  }

  // 아이템 생성
  async spawnItem(itemType, settings) {
    try {
      // 카메라 밖의 랜덤 위치 계산
      const spawnPosition = this.getRandomSpawnPosition();

      // 아이템 인스턴스 생성
      const ItemClass = settings.class;
      let item;

      // 다이아몬드인 경우 기본 GREEN 타입으로 생성
      if (ItemClass === Diamond) {
        item = new ItemClass(
          this.scene,
          spawnPosition.x,
          spawnPosition.z,
          "DIAMOND_GREEN"
        );
      } else {
        item = new ItemClass(this.scene, spawnPosition.x, spawnPosition.z);
      }

      // 아이템 초기화
      await item.init();

      // 아이템 목록에 추가
      this.items.push(item);

      // Utils.log(
      //   `${itemType} 아이템이 (${spawnPosition.x.toFixed(
      //     2
      //   )}, ${spawnPosition.z.toFixed(2)}) 위치에 생성되었습니다.`
      // );
    } catch (error) {
      Utils.log(`${itemType} 아이템 생성 실패: ${error.message}`, "error");
    }
  }

  // 카메라 밖의 랜덤 위치 계산
  getRandomSpawnPosition() {
    if (!this.player || !this.camera) {
      // 플레이어나 카메라가 없으면 기본 위치 반환
      return {
        x: Utils.randomBetween(-10, 10),
        z: Utils.randomBetween(-10, 10),
      };
    }

    // 플레이어 위치 기준으로 카메라 영역 계산
    const playerPos = this.player.position;
    const viewSize = this.cameraViewSize;
    const margin = this.spawnMargin;

    // 카메라 경계 계산 (직교 카메라 기준)
    const left = playerPos.x - viewSize;
    const right = playerPos.x + viewSize;
    const top = playerPos.z + viewSize;
    const bottom = playerPos.z - viewSize;

    // 카메라 밖 영역에서 랜덤 위치 선택
    // 4개 영역 중 하나를 선택: 위, 아래, 왼쪽, 오른쪽
    const areas = [
      // 위쪽 영역
      {
        x: [left - margin, right + margin],
        z: [top + margin, top + margin + 10],
      },
      // 아래쪽 영역
      {
        x: [left - margin, right + margin],
        z: [bottom - margin - 10, bottom - margin],
      },
      // 왼쪽 영역
      {
        x: [left - margin - 10, left - margin],
        z: [bottom - margin, top + margin],
      },
      // 오른쪽 영역
      {
        x: [right + margin, right + margin + 10],
        z: [bottom - margin, top + margin],
      },
    ];

    // 랜덤 영역 선택
    const selectedArea = Utils.randomChoice(areas);

    return {
      x: Utils.randomBetween(selectedArea.x[0], selectedArea.x[1]),
      z: Utils.randomBetween(selectedArea.z[0], selectedArea.z[1]),
    };
  }

  // 수집된 아이템들 정리
  cleanupCollectedItems() {
    // 한국어 주석: 수집 완료된 아이템들을 배열에서 제거
    const beforeCount = this.items.length;
    this.items = this.items.filter((item) => {
      // 다른 아이템들은 isCollected만 확인
      return !item.isCollected;
    });
    const removedCount = beforeCount - this.items.length;

    // 한국어 주석: 제거된 아이템이 있으면 로그 출력 (디버깅용)
    if (removedCount > 0 && GameConfig.DEBUG && GameConfig.DEBUG.ENABLED) {
      // Utils.log(`수집된 아이템 ${removedCount}개 정리 완료`);
    }
  }

  // 모든 아이템 제거
  clearAllItems() {
    this.items.forEach((item) => {
      item.destroy();
    });
    this.items = [];
    // Utils.log("모든 아이템이 제거되었습니다.");
  }

  // 현재 아이템 상태 정보
  getStatus() {
    const status = {
      totalItems: this.items.length,
      itemsByType: {},
    };

    Object.keys(this.spawnSettings).forEach((itemType) => {
      status.itemsByType[itemType] = this.getItemCountByType(itemType);
    });

    return status;
  }

  // 모든 다이아몬드에 자석 효과 적용 - 플레이어 위치 전달
  activateMagnetForAllDiamonds() {
    let activatedCount = 0;

    // 플레이어 위치 가져오기
    const playerPosition = this.player ? this.player.position : null;
    if (!playerPosition) {
      Utils.log(
        "플레이어 위치를 찾을 수 없어 자석 효과를 적용할 수 없습니다.",
        "error"
      );
      return 0;
    }

    this.items.forEach((item) => {
      if (item instanceof Diamond && !item.isCollected && item.isLoaded) {
        item.activateMagnet(playerPosition, this.player);
        activatedCount++;
      }
    });

    Utils.log(
      `🧲 ${activatedCount}개의 다이아몬드에 자석 효과가 적용되었습니다!`
    );
    return activatedCount;
  }

  // 모든 다이아몬드의 자석 효과 해제
  deactivateMagnetForAllDiamonds() {
    this.items.forEach((item) => {
      if (item instanceof Diamond && !item.isCollected) {
        item.deactivateMagnet();
      }
    });

    Utils.log("모든 다이아몬드의 자석 효과가 해제되었습니다.");
  }

  // 아이템 생성 중단
  stopSpawning() {
    this.isSpawningEnabled = false;
    Utils.log("아이템 생성이 중단되었습니다.");
  }

  // 아이템 생성 재개
  resumeSpawning() {
    this.isSpawningEnabled = true;
    Utils.log("아이템 생성이 재개되었습니다.");
  }

  // 동료 관리자 설정
  setCompanionManager(companionManager) {
    this.companionManager = companionManager;
  }
}

// webpack 빌드를 위해 default export 추가
export default ItemManager;
